import { useCallback } from 'react';
import { WorkOrderModuleCategoriesSettings } from 'lane-shared/domains/workOrder';
import { Channel } from 'application-types';
import {
  getBrowserTimeZone,
  getChannelTimezone,
} from 'domains/workOrder/serviceRequest/helpers/getChannelTimezone';
import { TeamCoverageEntry } from './coverageHoursTeamUtils';
import { buildCoverageHoursSavePayload } from './buildCoverageHoursSavePayload';
import { CoverageHoursIssueTypeOption } from './useCoverageHoursOptions';
import { useCreateCoverageHours } from './useCreateCoverageHours';

export type SaveCoverageHoursInput = {
  categoryValue: string;
  issueTypeValue: string;
  selectedCategory: WorkOrderModuleCategoriesSettings | undefined;
  issueTypeOptions: CoverageHoursIssueTypeOption[];
  savedTeams: TeamCoverageEntry[];
};

export type UseSaveCoverageHoursResult = {
  saveCoverageHours: (input: SaveCoverageHoursInput) => Promise<void>;
};

export function useSaveCoverageHours(
  channelId: string,
  channel?: Channel
): UseSaveCoverageHoursResult {
  const { createCoverageHours } = useCreateCoverageHours();

  const saveCoverageHours = useCallback(
    async ({
      categoryValue,
      issueTypeValue,
      selectedCategory,
      issueTypeOptions,
      savedTeams,
    }: SaveCoverageHoursInput) => {
      const timezone =
        (channel?.address ? getChannelTimezone(channel.address) : null) ??
        getBrowserTimeZone();

      const { issues, teams, shouldAlsoCreateNone } =
        buildCoverageHoursSavePayload({
          issueTypeValue,
          selectedCategory,
          issueTypeOptions,
          savedTeams,
        });

      await createCoverageHours({
        channelId,
        category: categoryValue,
        issues,
        timezone,
        teams,
      });

      if (shouldAlsoCreateNone) {
        // issues can only mean "one per listed issue" or "a single
        // category-level routing" when empty - never both in one request.
        await createCoverageHours({
          channelId,
          category: categoryValue,
          issues: [],
          timezone,
          teams,
        });
      }
    },
    [channelId, channel, createCoverageHours]
  );

  return { saveCoverageHours };
}
